import { useState, useMemo, Fragment, useCallback } from 'react';
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { DatePickerCell } from '@/components/ui/date-picker';
import api from '@/lib/api';
import { toast } from 'sonner';
import MacroLinkDropdown from '@/components/rollup/MacroLinkDropdown';

const STATUS_COLORS = {
  ON_TRACK: { bg: 'rgba(16,185,129,0.15)', color: '#10B981' },
  DELAYED: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444' },
  AHEAD: { bg: 'rgba(59,130,246,0.15)', color: '#3B82F6' },
  COMPLETED: { bg: 'rgba(16,185,129,0.2)', color: '#059669' },
  PO_PENDING: { bg: 'rgba(196,168,255,0.15)', color: '#C4A8FF' },
  IN_TRANSIT: { bg: 'rgba(56,189,248,0.15)', color: '#38BDF8' },
  IN_PROGRESS: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B' }
};

const PHASE_COLORS = {
  PRE_ORDER: { bg: 'rgba(94,234,212,0.15)', color: '#5EEAD4' },
  POST_ORDER: { bg: 'rgba(196,168,255,0.15)', color: '#C4A8FF' },
  COMPLETED: { bg: 'rgba(16,185,129,0.2)', color: '#059669' }
};

function ProgressBar({ value, width = '100%' }) {
  const pct = Math.min(Math.max(value || 0, 0), 100);
  return (
    <div className="flex items-center gap-2" style={{ width }}>
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--card-bg-alt)' }}>
        <div className="h-full rounded-full transition-all" style={{
          width: `${pct}%`,
          background: pct === 100 ? '#10B981' : pct > 70 ? '#3B82F6' : pct > 30 ? '#F59E0B' : '#EF4444'
        }} />
      </div>
      <span className="text-xs font-mono w-10 text-right" style={{ color: 'var(--text-muted)' }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

function GateTimeline({ gates, phase, phaseWeight, entryId, projectId, onDateUpdate }) {
  if (!gates?.length) return null;
  
  const handleDateChange = async (gateId, dateType, newValue) => {
    const routePhase = phase === 'pre' ? 'pre-order' : 'post-order';
    try {
      await api.put(`/v1/projects/${projectId}/mci/entries/${entryId}/${routePhase}/${gateId}`, {
        [dateType]: newValue
      });
      toast.success(`${dateType.replace('Date', '')} date updated`);
      if (onDateUpdate) onDateUpdate();
      window.dispatchEvent(new CustomEvent('mci-register-updated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update date');
    }
  };

  return (
    <div className="space-y-1">
      {gates.map((sg, i) => {
        const effectiveWt = ((phaseWeight * sg.weightagePercent) / 100).toFixed(1);
        const st = STATUS_COLORS[sg.status] || STATUS_COLORS.PO_PENDING;
        return (
          <div key={i} className="grid gap-2 items-center text-xs" style={{ gridTemplateColumns: '140px 40px 80px 80px 80px 70px' }}>
            <span className="truncate" style={{ color: 'var(--text-primary)' }} title={sg.stageGateName}>{sg.stageGateName}</span>
            <span className="text-center font-mono" style={{ color: 'var(--text-muted)' }}>{effectiveWt}%</span>
            <DatePickerCell value={sg.plannedDate} dateColor="var(--text-muted)"
              onChange={(v) => handleDateChange(sg.stageGateId, 'plannedDate', v)} />
            <DatePickerCell value={sg.forecastDate} dateColor="#F59E0B"
              onChange={(v) => handleDateChange(sg.stageGateId, 'forecastDate', v)} />
            <DatePickerCell value={sg.actualDate} dateColor="#10B981"
              onChange={(v) => handleDateChange(sg.stageGateId, 'actualDate', v)} />
            <span className="px-1.5 py-0.5 rounded text-center" style={{ background: st.bg, color: st.color, fontSize: '10px' }}>
              {sg.status?.replace('_', ' ')}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ExpandedDetail({ entry, projectId, onDateUpdate }) {
  const preWt = 30;
  const postWt = 70;

  return (
    <div className="px-6 py-4 space-y-4" style={{ background: 'var(--card-bg-alt)' }} data-testid={`detail-${entry.mciLineNumber}`}>
      <div className="flex gap-4 text-xs flex-wrap" style={{ color: 'var(--text-muted)' }}>
        <span>MR: <strong style={{ color: 'var(--text-primary)' }}>{entry.mrNumber || '—'}</strong></span>
        <span>PO: <strong style={{ color: 'var(--text-primary)' }}>{entry.poNumber || '—'}</strong></span>
        <span>Vendor: <strong style={{ color: 'var(--text-primary)' }}>{entry.vendor || '—'}</strong></span>
        {entry.poValue != null && <span>PO Value: <strong style={{ color: 'var(--text-primary)' }}>{entry.poCurrency || 'USD'} {entry.poValue?.toLocaleString()}</strong></span>}
        {entry.plannedLeadTimeDays != null && <span>Lead Time: <strong>{entry.plannedLeadTimeDays}d plan</strong>{entry.actualLeadTimeDays != null && <> / <strong>{entry.actualLeadTimeDays}d actual</strong></>}</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-3 rounded-lg border" style={{ borderColor: 'rgba(94,234,212,0.3)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold" style={{ color: '#5EEAD4' }}>PRE-ORDER ({preWt}%)</span>
            <ProgressBar value={entry.preOrderProgressPercent} width="120px" />
          </div>
          <div className="text-xs mb-1 grid gap-2" style={{ gridTemplateColumns: '140px 40px 80px 80px 80px 70px', color: 'var(--text-disabled)' }}>
            <span>Gate</span><span className="text-center">Eff%</span><span>Planned</span><span>Forecast</span><span>Actual</span><span className="text-center">Status</span>
          </div>
          <GateTimeline gates={entry.preOrderProgress} phase="pre" phaseWeight={preWt} entryId={entry.id} projectId={projectId} onDateUpdate={onDateUpdate} />
        </div>

        <div className="p-3 rounded-lg border" style={{ borderColor: 'rgba(196,168,255,0.3)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold" style={{ color: '#C4A8FF' }}>POST-ORDER ({postWt}%)</span>
            <ProgressBar value={entry.postOrderProgressPercent} width="120px" />
          </div>
          <div className="text-xs mb-1 grid gap-2" style={{ gridTemplateColumns: '140px 40px 80px 80px 80px 70px', color: 'var(--text-disabled)' }}>
            <span>Gate</span><span className="text-center">Eff%</span><span>Planned</span><span>Forecast</span><span>Actual</span><span className="text-center">Status</span>
          </div>
          <GateTimeline gates={entry.postOrderProgress} phase="post" phaseWeight={postWt} entryId={entry.id} projectId={projectId} onDateUpdate={onDateUpdate} />
        </div>
      </div>

      <div className="flex items-center gap-4 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Overall:</span>
          <ProgressBar value={entry.overallProgress} width="150px" />
        </div>
        {entry.leadTimeVarianceDays != null && (
          <span className="text-xs" style={{ color: entry.leadTimeVarianceDays > 0 ? '#EF4444' : '#10B981' }}>
            Variance: {entry.leadTimeVarianceDays > 0 ? '+' : ''}{entry.leadTimeVarianceDays} days
          </span>
        )}
      </div>
    </div>
  );
}

function MCIRegisterList({ entries, loading, search, onSearchChange, projectId, onRefresh }) {
  const [expandedId, setExpandedId] = useState(null);
  const [sortField, setSortField] = useState('mciLineNumber');
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    const arr = [...(entries || [])];
    arr.sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [entries, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortHeader = ({ field, children, width }) => (
    <th className="px-3 py-2.5 text-left font-medium cursor-pointer select-none" style={{ color: 'var(--text-muted)', width }}
      onClick={() => handleSort(field)}>
      <span className="flex items-center gap-1">{children}
        {sortField === field && <span className="text-xs">{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>}
      </span>
    </th>
  );

  return (
    <div data-testid="mci-register-list">
      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-sm" data-testid="mci-register-table">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="w-8 px-2"></th>
              <SortHeader field="mciLineNumber" width="90px">MCI#</SortHeader>
              <SortHeader field="materialCategory" width="75px">Cat</SortHeader>
              <th className="px-3 py-2.5 text-left font-medium" style={{ color: 'var(--text-muted)', width: '150px' }}>Tag / Material Code</th>
              <SortHeader field="materialDescription" width="200px">Description</SortHeader>
              <SortHeader field="materialTypeName" width="110px">Type</SortHeader>
              <SortHeader field="discipline" width="90px">Discipline</SortHeader>
              <th className="px-3 py-2.5 text-left font-medium" style={{ color: 'var(--text-muted)', width: '90px' }}>MR No.</th>
              <th className="px-3 py-2.5 text-left font-medium" style={{ color: 'var(--text-muted)', width: '90px' }}>PO No.</th>
              <SortHeader field="currentPhase" width="95px">Phase</SortHeader>
              <SortHeader field="overallProgress" width="130px">Progress</SortHeader>
              <SortHeader field="overallStatus" width="90px">Status</SortHeader>
              <th className="px-3 py-2.5 text-center font-medium" style={{ color: 'var(--text-muted)', width: '140px' }}>Macro Link</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(e => {
              const isExpanded = expandedId === e.id;
              const st = STATUS_COLORS[e.overallStatus] || STATUS_COLORS.PO_PENDING;
              const ph = PHASE_COLORS[e.currentPhase] || PHASE_COLORS.PRE_ORDER;
              return (
                <Fragment key={e.id}>
                  <tr className="border-t cursor-pointer transition-colors hover:opacity-90" style={{ borderColor: 'var(--border-color)' }}
                    onClick={() => setExpandedId(isExpanded ? null : e.id)} data-testid={`row-${e.mciLineNumber}`}>
                    <td className="px-2 py-2">
                      {isExpanded ? <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /> : <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: 'var(--accent-primary)' }}>
                      {e.mciLineNumber}
                      {e.defaultReceiptLocation && (
                        <div className="flex items-center gap-0.5 mt-0.5" title={`Delivery / GRN / IQC location: ${e.defaultReceiptLocation}`}
                          data-testid={`row-loc-${e.mciLineNumber}`}>
                          <MapPin className="w-2.5 h-2.5" style={{ color: 'var(--tone-navy-text)' }} />
                          <span className="text-[9px] font-normal truncate max-w-[110px]" style={{ color: 'var(--tone-navy-text)' }}>{e.defaultReceiptLocation}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium" style={{
                        background: e.materialCategory === 'BULK' ? 'rgba(94,234,212,0.15)' : 'rgba(196,168,255,0.15)',
                        color: e.materialCategory === 'BULK' ? '#5EEAD4' : '#C4A8FF'
                      }}>{e.materialCategory}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono" style={{ color: 'var(--text-primary)' }}
                      data-testid={`mci-code-${e.mciLineNumber}`}
                      title={e.materialCategory === 'BULK' ? 'Bulk item code' : 'Tag number'}>
                      {e.tagNumber || e.bulkItemCode || e.materialCode || '\u2014'}
                    </td>
                    <td className="px-3 py-2 text-xs truncate max-w-[200px]" style={{ color: 'var(--text-primary)' }} title={e.materialDescription}>{e.materialDescription}</td>
                    <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>{e.materialTypeName}</td>
                    <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>{e.discipline}</td>
                    <td className="px-3 py-2 text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{e.mrNumber || '\u2014'}</td>
                    <td className="px-3 py-2 text-xs font-mono" style={{ color: e.poNumber ? 'var(--text-primary)' : 'var(--text-disabled)' }}>{e.poNumber || '\u2014'}</td>
                    <td className="px-3 py-2">
                      <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: ph.bg, color: ph.color }}>{e.currentPhase?.replace('_', '-')}</span>
                    </td>
                    <td className="px-3 py-2"><ProgressBar value={e.overallProgress} /></td>
                    <td className="px-3 py-2">
                      <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: st.bg, color: st.color }}>{e.overallStatus?.replace('_', ' ')}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <MacroLinkDropdown
                        projectId={projectId}
                        microModule="MCI"
                        microActivityId={e.id}
                        microActivityCode={e.mciLineNumber}
                        microActivityName={e.materialDescription}
                      />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr><td colSpan={13}><ExpandedDetail entry={e} projectId={projectId} onDateUpdate={onRefresh} /></td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {sorted.length === 0 && (
          <div className="py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
            {loading ? 'Loading...' : 'No MCI entries found. Import from Excel or create entries manually.'}
          </div>
        )}
      </div>
    </div>
  );
}

export default MCIRegisterList;
